import { useEffect, useMemo, useState } from "react";
import { invApi, transfersApi, type StockAt, type StockTransfer } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad, errText } from "../ui/useLoad";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { Combobox } from "../ui/Combobox";
import { fmtDate } from "../companies/CompanyPicker";
import { iso } from "../reports/period";
import { fmtAmount } from "../lib/money";

// IN11 Stock transfers: stock sent from one warehouse to another (sql/258_b). Posted at once, at cost (no accounting
// entry: the stock account is the item's, not the warehouse's); lot-tracked items go first-to-expire unless a batch is
// chosen. A transfer can be cancelled while the stock is still where it was sent.
type Line = { variant: string | null; lot: string; qty: string };
const blank = (): Line => ({ variant: null, lot: "", qty: "" });

export function StockTransfers() {
  const { company, ref } = useCompany();
  const can = ref?.can_edit ?? false;
  const t = new Date();
  const [rev, setRev] = useState(0);
  const items = useLoad(() => invApi.entryItems(company.id), [company.id]);
  const list = useLoad(() => transfersApi.list(company.id, iso(new Date(t.getFullYear() - 1, t.getMonth(), 1)), iso(t)), [company.id, rev]);   // eslint-disable-line react-hooks/exhaustive-deps
  const whs = items.data?.warehouses ?? [];
  const [date, setDate] = useState(iso(t));
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [reference, setReference] = useState(""); const [reason, setReason] = useState("");
  const [lines, setLines] = useState<Line[]>([blank()]);
  const [stock, setStock] = useState<StockAt[] | null>(null);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null); const [msg, setMsg] = useState<string | null>(null);
  const [open, setOpen] = useState<StockTransfer | null>(null);
  const [why, setWhy] = useState("");

  useEffect(() => { if (!from && whs.length) setFrom(whs[0].id); }, [whs, from]);
  useEffect(() => {
    setStock(null);
    if (from && date) transfersApi.stockAt(company.id, from, date).then(setStock).catch(() => setStock([]));
  }, [company.id, from, date, rev]);

  // what is at the sending warehouse on the date, by item (all its batches) and by batch
  const byItem = useMemo(() => {
    const m = new Map<string, { qty: number; unit: string; lots: StockAt[] }>();
    for (const s of stock ?? []) {
      const e = m.get(s.variant_id) ?? { qty: 0, unit: s.unit, lots: [] };
      e.qty += Number(s.qty); e.lots.push(s); m.set(s.variant_id, e);
    }
    return m;
  }, [stock]);
  const opts = (items.data?.items ?? []).map((i) => ({ value: i.id, label: i.name, hint: `${i.sku} · ${byItem.get(i.id)?.qty ?? 0} here` }));
  const avail = (l: Line) => { const e = l.variant ? byItem.get(l.variant) : undefined; return l.lot ? Number(e?.lots.find((x) => x.lot_id === l.lot)?.qty ?? 0) : e?.qty ?? 0; };
  const nameOf = (id: string | null) => items.data?.items.find((i) => i.id === id)?.name ?? "the item";

  // what is wrong, field by field (outlined once a post has been tried)
  const filled = lines.filter((l) => l.variant || l.qty);
  const problems: { key: string; text: string }[] = [];
  if (!from) problems.push({ key: "from", text: "Choose the warehouse it goes from." });
  if (!to) problems.push({ key: "to", text: "Choose the warehouse it goes to." });
  else if (to === from) problems.push({ key: "to", text: "It goes to the same warehouse it comes from: choose another one." });
  if (!filled.length) problems.push({ key: "line-0-item", text: "Add at least one item to transfer." });
  filled.forEach((l) => {
    const i = lines.indexOf(l);
    if (!l.variant) problems.push({ key: `line-${i}-item`, text: `Line ${i + 1}: choose the item.` });
    else if (!(Number(l.qty) > 0)) problems.push({ key: `line-${i}-qty`, text: `Line ${i + 1}: enter how many of ${nameOf(l.variant)} to send.` });
    else if (stock && Number(l.qty) > avail(l)) problems.push({ key: `line-${i}-qty`,
      text: `Line ${i + 1}: only ${avail(l)} ${byItem.get(l.variant)?.unit ?? ""} of ${nameOf(l.variant)} is at the sending warehouse on ${fmtDate(date)}.` });
  });
  const bad = (k: string) => (tried && problems.some((p) => p.key === k) ? "bad" : "");

  const post = async () => {
    setTried(true); setErr(null); setMsg(null);
    if (problems.length) return;
    setBusy(true);
    try {
      const r = await transfersApi.save(company.id, { date, from_location: from, to_location: to, reference, reason,
        lines: filled.map((l) => ({ variant_id: l.variant, qty: Number(l.qty), lot_id: l.lot || undefined })) });
      setMsg(`Transfer ${r.number} posted: ${r.lines?.length ?? 0} line${r.lines?.length === 1 ? "" : "s"} from ${r.from} to ${r.to}.`);
      setLines([blank()]); setReference(""); setReason(""); setTried(false); setRev(rev + 1);
    } catch (x) { setErr(errText(x)); } finally { setBusy(false); }
  };
  const show = (id: string) => transfersApi.view(company.id, id).then((v) => { setOpen(v); setWhy(""); }).catch((e) => setErr(errText(e)));
  const cancel = async () => {
    if (!open) return;
    if (!why.trim()) { setErr("Say why the transfer is cancelled."); return; }
    try { setOpen(await transfersApi.cancel(company.id, open.id, why)); setRev(rev + 1); setErr(null); } catch (x) { setErr(errText(x)); }
  };

  return (
    <section className="page">
      <PageHead crumb="Inventory" title="Stock transfers" code="IN11" />
      <p className="muted small">Send stock from one warehouse to another. It moves at what it cost and keeps its age; no accounting entry is needed, since the stock account belongs to the item, not the warehouse. Batches go first-to-expire unless you pick one.</p>
      {can && whs.length < 2 && items.data && <p className="msg">You have {whs.length} warehouse{whs.length === 1 ? "" : "s"}. Add another under Inventory › Units, categories and warehouses (IN10) to transfer stock between them.</p>}
      {can && (
        <form className="card panel" onSubmit={(e) => { e.preventDefault(); void post(); }}>
          <div className="fields inline">
            <label>Date<input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
            <label className={bad("from")}><span className="req">From</span><select value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From warehouse">
              <option value="">Choose…</option>{whs.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</select></label>
            <label className={bad("to")}><span className="req">To</span><select value={to} onChange={(e) => setTo(e.target.value)} aria-label="To warehouse">
              <option value="">Choose…</option>{whs.filter((w) => w.id !== from).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</select></label>
            <label>Reference<input maxLength={60} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Challan, truck no." /></label>
            <label className="wide-field">Note<input maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. restocking Mumbai" /></label>
          </div>
          <table className="grid-table lines">
            <thead><tr><th>Item</th><th>Batch</th><th className="num">At the sending warehouse</th><th className="num">Quantity</th><th></th></tr></thead>
            <tbody>{lines.map((l, i) => {
              const up = (p: Partial<Line>) => setLines(lines.map((x, j) => (j === i ? { ...x, ...p } : x)));
              const e = l.variant ? byItem.get(l.variant) : undefined;
              const tracked = (e?.lots.length ?? 0) > 1 || !!e?.lots[0]?.batch;
              return (
                <tr key={i}>
                  <td className={bad(`line-${i}-item`)}><Combobox options={opts} value={l.variant} onChange={(v) => up({ variant: v, lot: "" })} /></td>
                  <td>{tracked ? <select value={l.lot} onChange={(ev) => up({ lot: ev.target.value })} aria-label="Batch">
                    <option value="">First to expire</option>{e!.lots.map((x) => <option key={x.lot_id} value={x.lot_id}>{x.batch ?? "No batch"}{x.expiry ? ` · exp ${fmtDate(x.expiry)}` : ""} · {x.qty}</option>)}
                  </select> : <span className="muted small">—</span>}</td>
                  <td className="num muted">{l.variant ? (stock ? `${avail(l)} ${e?.unit ?? ""}` : "…") : ""}</td>
                  <td className={`num ${bad(`line-${i}-qty`)}`}><input className="num" inputMode="decimal" value={l.qty} aria-label="Quantity" onChange={(ev) => up({ qty: ev.target.value })} /></td>
                  <td><button type="button" className="link" aria-label="Remove line" onClick={() => setLines(lines.length > 1 ? lines.filter((_, j) => j !== i) : [blank()])}>✕</button></td>
                </tr>);
            })}</tbody>
          </table>
          <div className="row"><button type="button" onClick={() => setLines([...lines, blank()])}>Add line</button>
            <button className="primary" disabled={busy}>{busy ? "Posting…" : "Post transfer"}</button></div>
          {tried && problems.length > 0 && <div className="msg err problems" role="alert"><b>Before it can be posted:</b><ul>{problems.map((p, i) => <li key={i}>{p.text}</li>)}</ul></div>}
        </form>
      )}
      <ErrorNote text={err} />{msg && <p className="msg ok">{msg}</p>}
      {open && (
        <div className="card panel">
          <div className="row"><strong>{open.number}</strong><span>{fmtDate(open.date)} · {open.from} → {open.to}</span>
            {open.status === "cancelled" && <span className="tag">cancelled</span>}<button className="link" onClick={() => setOpen(null)}>Close</button></div>
          {open.reference && <p className="muted small">Reference {open.reference}{open.reason ? ` · ${open.reason}` : ""}</p>}
          {open.cancel_reason && <p className="muted small">Cancelled: {open.cancel_reason}</p>}
          <table className="grid-table compact"><thead><tr><th>Item</th><th>Batch</th><th className="num">Quantity</th><th className="num">Value at cost</th></tr></thead>
            <tbody>{(open.lines ?? []).map((x, i) => <tr key={i}><td>{x.item} <span className="muted small">{x.sku}</span></td><td>{x.batch ?? ""}{x.expiry ? ` · exp ${fmtDate(x.expiry)}` : ""}</td>
              <td className="num">{x.qty} {x.unit}</td><td className="num">{x.value != null ? fmtAmount(x.value) : ""}</td></tr>)}</tbody></table>
          {open.can_cancel && <div className="fields inline"><label className="wide-field">Why cancel<input value={why} onChange={(e) => setWhy(e.target.value)} placeholder="e.g. sent by mistake" /></label>
            <button type="button" onClick={() => void cancel()}>Cancel transfer</button></div>}
        </div>
      )}
      <h3>Transfers</h3>
      {!list.data ? <Loading /> : list.data.length === 0 ? <p className="muted">None in the last year.</p> : (
        <table className="grid-table"><thead><tr><th>Number</th><th>Date</th><th>From</th><th>To</th><th>Reference</th><th className="num">Items</th><th className="num">Quantity</th><th>Status</th></tr></thead>
          <tbody>{list.data.map((x) => <tr key={x.id} className="link-row" onClick={() => void show(x.id)}>
            <td>{x.number}</td><td>{fmtDate(x.date)}</td><td>{x.from}</td><td>{x.to}</td><td className="muted">{x.reference ?? ""}</td>
            <td className="num">{x.items}</td><td className="num">{x.qty}</td><td>{x.status === "cancelled" ? <span className="tag">cancelled</span> : "Posted"}</td></tr>)}</tbody></table>)}
    </section>
  );
}
